import { useState, useEffect } from 'react';
import { Box, TextField, Button } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useApi, identityApiRef, configApiRef } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  SidebarItem,
  useSidebarOpenState,
} from '@backstage/core-components';
import PersonIcon from '@material-ui/icons/Person';
// import { Link } from '@backstage/core-components';
// import SecurityIcon from '@material-ui/icons/Security';

const useStyles = makeStyles(theme => ({
  container: {
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  textField: {
    width: '100%',
  },
  clearButton: {
    marginTop: theme.spacing(1),
    width: '100%',
  },
  manageRolesButton: {
    marginTop: theme.spacing(1),
    width: '100%',
  },
}));

export const ImpersonationControl = () => {
  const classes = useStyles();
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  const config = useApi(configApiRef);

  const { isOpen } = useSidebarOpenState();

  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  const [impersonatedUserInput, setImpersonatedUserInput] = useState<string>(
    localStorage.getItem('backstage.impersonatedUser') || ''
  );

  // Verificar permisos con el usuario REAL (ignorando impersonación)
  useEffect(() => {
    let cancelled = false;

    const checkRealUserPermission = async () => {
      try {
        setLoading(true);

        // Get REAL user identity (not impersonated)
        const identity = await identityApi.getBackstageIdentity();
        const userEntityRef = identity.userEntityRef;
        const realUserName = userEntityRef.split('/')[1];

        // Get user email from catalog
        let realUserEmail = realUserName; // Fallback to username
        try {
          const userEntity = await catalogApi.getEntityByRef(userEntityRef);
          const profile = userEntity?.spec?.profile as any;
          if (profile?.email) {
            realUserEmail = profile.email;
          }
        } catch (err) {
          console.warn('⚠️ Could not fetch user entity for impersonation control, using username instead:', err);
        }

        // Check platform_admin relation on platform:main
        const resource = 'platform:main';

        console.log('🔐 Checking REAL user platform_admin permission for impersonation control:', {
          user: `user:${realUserEmail}`,
          relation: 'platform_admin',
          resource: resource,
        });

        // Call backend API using REAL user (not impersonated)
        const backendUrl = config.getString('backend.baseUrl');
        const token = (await identityApi.getCredentials()).token;

        const response = await fetch(`${backendUrl}/api/openfga-client/check-permission`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            user: `user:${realUserEmail}`,  // Using REAL user email, NOT impersonated
            action: 'platform_admin',
            resource: resource,
          }),
        });

        if (!response.ok) {
          throw new Error(`Failed to check permission: ${response.statusText}`);
        }

        const data = await response.json();

        if (!cancelled) {
          setIsPlatformAdmin(data.allowed || false);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Error checking real user permission:', err);
          setIsPlatformAdmin(false);
          setLoading(false);
        }
      }
    };

    checkRealUserPermission();

    return () => {
      cancelled = true;
    };
  }, [identityApi, catalogApi, config]);

  // No mostrar el control hasta verificar permisos o si no es admin
  if (loading || !isPlatformAdmin) {
    return null;
  }

  const handleImpersonateBlur = () => {
    const value = impersonatedUserInput.trim();
    if (value) {
      localStorage.setItem('backstage.impersonatedUser', value);
      // Reload to apply impersonation
      window.location.reload();
    } else {
      localStorage.removeItem('backstage.impersonatedUser');
      window.location.reload();
    }
  };

  const clearImpersonation = () => {
    setImpersonatedUserInput('');
    localStorage.removeItem('backstage.impersonatedUser');
    window.location.reload();
  };

  if (!isOpen) {
    return (
      <SidebarItem icon={PersonIcon} to=""
        onClick={(e: any) => {
          e.preventDefault();
          e.stopPropagation();
        }} />
    );
  }

  return (
    <Box className={classes.container}>
      <TextField
        label="🎭 Impersonate User"
        variant="outlined"
        size="small"
        value={impersonatedUserInput}
        onChange={e => setImpersonatedUserInput(e.target.value)}
        onBlur={handleImpersonateBlur}
        onKeyPress={e => {
          if (e.key === 'Enter') {
            handleImpersonateBlur();
          }
        }}
        placeholder="username"
        className={classes.textField}
      />
      {impersonatedUserInput && (
        <Button
          onClick={clearImpersonation}
          variant="outlined"
          size="small"
          className={classes.clearButton}
        >
          Clear Impersonation
        </Button>
      )}
      {/* <Link to="/manage-roles">
        <Button
          variant="outlined"
          size="small"
          startIcon={<SecurityIcon />}
          className={classes.manageRolesButton}
        >
          Manage Roles
        </Button>
      </Link> */}
    </Box>
  );
};
